import { Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, ParamMap, UrlSegment } from '@angular/router';
import { Subject } from 'rxjs';
import { mergeMap, take } from 'rxjs/operators';
import { ApplicationLicenseService } from '~app/core/data/application-license.service';
import { FilesService } from '~app/core/data/files.service';
import { LicenseService } from '~app/core/data/license.service';
import { ProgressDialogComponent } from '~app/core/utils/progress-dialog/progress-dialog.component';
import { ROUTE_ANIMATIONS_ELEMENTS } from '~core/core.module';

@Component({
  selector: 'ngx-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent {
  routeAnimationsElements = ROUTE_ANIMATIONS_ELEMENTS;

  downloadingFile = false;
  private progress$ = new Subject();

  constructor(
    private _appLicenseService: ApplicationLicenseService,
    private _dialog: MatDialog,
    private _licenseService: LicenseService,
    private _route: ActivatedRoute,
    private _filesService: FilesService
  ) {
    this._route.paramMap.subscribe((params: ParamMap) => {
      if (params.get('id')) {
        this._route.parent.url.subscribe((url: UrlSegment[]) => {
          if (url[0].path === 'download') {
            // Download
            this.downloadVersion(+params.get('id'));
          } else if (url[0].path === 'getlatest') {
            this.downloadLatest(+params.get('id'));
          }
        });
      }
    });
  }

  downloadVersion(fileId: number) {
    this.downloadingFile = true;
    this.progress$.next(0);

    this._filesService
      .get(fileId)
      .pipe(take(1))
      .subscribe(
        version => {
          const dialogRef = this._dialog.open(ProgressDialogComponent, {
            maxWidth: '650px',
            width: '90%',
            disableClose: true,
            data: {
              message: 'Downloading ' + version.name,
              progress$: this.progress$
            }
          });

          dialogRef
            .afterOpened()
            .pipe(take(1))
            .subscribe(() => {
              this._filesService.download(fileId).subscribe(
                event => {
                  if (event.blob) {
                    // It is necessary to create a new blob object with mime-type explicitly set
                    // otherwise only Chrome works like it should
                    const newBlob = new Blob([event.blob], {
                      type: event.type
                    });

                    dialogRef.close();

                    // IE doesn't allow using a blob object directly as link href
                    // instead it is necessary to use msSaveOrOpenBlob
                    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                      window.navigator.msSaveOrOpenBlob(newBlob, version.name);
                      return;
                    }

                    // For other browsers:
                    // Create a link pointing to the ObjectURL containing the blob.
                    const data = window.URL.createObjectURL(newBlob);

                    const link = document.createElement('a');
                    link.href = data;
                    link.download = version.name;
                    // this is necessary as link.click() does not work on the latest firefox
                    link.dispatchEvent(
                      new MouseEvent('click', {
                        bubbles: true,
                        cancelable: true,
                        view: window
                      })
                    );

                    setTimeout(function() {
                      // For Firefox it is necessary to delay revoking the ObjectURL
                      window.URL.revokeObjectURL(data);
                      link.remove();
                    }, 100);
                    this.downloadingFile = false;
                  } else {
                    this.progress$.next(event.progress);
                  }
                },
                err => {
                  this.downloadingFile = false;
                  dialogRef.close();
                }
              );
            });
        },
        err => {
          // console.log(err);
        }
      );
  }

  downloadLatest(licenseId: number) {
    this._licenseService
      .get(licenseId)
      .pipe(
        take(1),
        mergeMap(license => {
          return this._appLicenseService.latestVersion(
            license.applicationId,
            license.licenseRecordId
          );
        })
      )
      .subscribe(versions => {
        if (versions.length > 0) {
          const version = versions[versions.length - 1];
          if (version.fileId > 0) {
            const dialogRef = this._dialog.open(ProgressDialogComponent, {
              maxWidth: '650px',
              width: '90%',
              disableClose: true,
              data: {
                message: 'Downloading ' + version.name,
                progress$: this.progress$
              }
            });

            this.downloadingFile = true;
            this.progress$.next(0);

            dialogRef.afterOpened().subscribe(() => {
              this._filesService
                .download(version.fileId)
                .pipe(take(1))
                .subscribe(event => {
                  if (event.blob) {
                    // It is necessary to create a new blob object with mime-type explicitly set
                    // otherwise only Chrome works like it should
                    const newBlob = new Blob([event.blob], {
                      type: event.type
                    });

                    dialogRef.close();

                    // IE doesn't allow using a blob object directly as link href
                    // instead it is necessary to use msSaveOrOpenBlob
                    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                      window.navigator.msSaveOrOpenBlob(newBlob, version.name);
                      return;
                    }

                    // For other browsers:
                    // Create a link pointing to the ObjectURL containing the blob.
                    const data = window.URL.createObjectURL(newBlob);

                    const link = document.createElement('a');
                    link.href = data;
                    link.download = version.name;
                    // this is necessary as link.click() does not work on the latest firefox
                    link.dispatchEvent(
                      new MouseEvent('click', {
                        bubbles: true,
                        cancelable: true,
                        view: window
                      })
                    );

                    setTimeout(function() {
                      // For Firefox it is necessary to delay revoking the ObjectURL
                      window.URL.revokeObjectURL(data);
                      link.remove();
                    }, 100);
                  } else {
                    this.progress$.next(event.progress);
                  }
                });
            });
          }
        }
      });
  }
}
